// Messages Page for Iron Forums

import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { messagingAPI, usersAPI, jarvisAPI, forumsAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { isAdminRole, isFacilitatorRole, isLeadershipRole, roleLabel } from '../utils/roles';
import { MessageSquare, Send, Users, User, ChevronLeft, Plus, Search, X, Sparkles, ArrowLeft, Check, Building2, UserPlus, MoreHorizontal, Bell, BellOff, Archive, LogOut, AlertCircle, RefreshCw, ImagePlus } from 'lucide-react';
import type { Thread, Message, UserListItem, ChatMessage, ForumListItem, User as ForumUser } from '../types';
import MicButton from '../components/MicButton';
import AutosizeTextarea from '../components/AutosizeTextarea';
import AskIfMemberCards, { membersFromAskIfAction } from '../components/AskIfMemberCards';
import { useVisibilityPolling } from '../hooks/useVisibilityPolling';
import { useKeyboardInset } from '../hooks/useKeyboardInset';
import { notifyBadgesStale } from '../lib/badges';
import { setActiveChatThreadId } from '../lib/activeChat';
import { renderMessageContent } from '../lib/linkifyMessage';
import { messageTextProps } from '../lib/messageField';
import { ACCEPTED_SCREENSHOT_TYPES, acceptScreenshots, pasteScreenshots } from '../lib/screenshots';
import { LocalPreview, ScreenshotStrip } from '../components/TicketScreenshots';
import { AddReactionButton, EmojiPickerButton, ReactionChips } from '../components/MessageReactions';

const MAX_MESSAGE_PICTURES = 4;

// Fallback only; the real cap is read from the server (messaging/config/).
const DEFAULT_GROUP_MAX_MEMBERS = 10;

const GROUP_ROLE_FILTERS = [
  { value: '', label: 'All roles' },
  { value: 'member', label: 'Member' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'guest', label: 'Guest' },
  { value: 'facilitator', label: 'Facilitator' },
  { value: 'admin', label: 'Admin' },
];

function matchesRoleFilter(role: string, filter: string): boolean {
  if (!filter) return true;
  if (filter === 'admin') return isAdminRole(role);
  if (filter === 'facilitator') return isFacilitatorRole(role);
  return role === filter;
}

function matchesForumFilter(
  forums: { id: number }[] | undefined,
  filter: string,
): boolean {
  if (!filter) return true;
  const ids = (forums || []).map((forum) => forum.id);
  if (filter === 'none') return ids.length === 0;
  return ids.includes(Number(filter));
}

function forumNames(forums: { name: string }[] | undefined): string {
  return (forums || []).map((forum) => forum.name).join(', ');
}

type Recipient = {
  id: number;
  full_name: string;
  avatar_url?: string;
  role?: string;
  chapter?: string;
  is_paused?: boolean;
  limit_app_access?: boolean;
};

function PausedBadge({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold uppercase tracking-wide text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full ${className}`}
    >
      Paused
    </span>
  );
}

function LimitedAccessBadge({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold uppercase tracking-wide text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full ${className}`}
    >
      Limited access
    </span>
  );
}

function AccessBadges({
  person,
  className = '',
}: {
  person: { is_paused?: boolean; limit_app_access?: boolean };
  className?: string;
}) {
  return (
    <>
      {person.is_paused && <PausedBadge className={className} />}
      {person.limit_app_access && <LimitedAccessBadge className={className} />}
    </>
  );
}

function limitedAccessPartners(
  thread: Thread | null,
  draft: Recipient | null,
  selfId?: number,
): { id: number; name: string }[] {
  if (draft?.limit_app_access) {
    return [{ id: draft.id, name: draft.full_name }];
  }
  if (!thread) return [];
  return thread.participants
    .filter((person) => person.id !== selfId && person.limit_app_access)
    .map((person) => ({
      id: person.id,
      name: person.full_name || person.username,
    }));
}

type ConversationMenuProps = {
  thread: Thread;
  onToggleMute: (thread: Thread) => Promise<void>;
  onRemove: (thread: Thread) => Promise<void>;
  onInfo?: (thread: Thread) => void;
  disabled?: boolean;
  align?: 'left' | 'right';
};

function ConversationMenu({
  thread,
  onToggleMute,
  onRemove,
  onInfo,
  disabled = false,
  align = 'right',
}: ConversationMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const removeLabel = thread.kind === 'group' ? 'Leave conversation' : 'Remove conversation';

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        containerRef.current?.querySelector<HTMLButtonElement>('[aria-haspopup="menu"]')?.focus();
      }
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        disabled={disabled}
        className="p-2 rounded-lg text-gray-500 hover:text-navy hover:bg-gray-100 transition disabled:opacity-50"
        aria-label={`Manage ${thread.name || 'conversation'}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={20} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute top-full mt-1 z-40 w-56 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {onInfo && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onInfo(thread);
              }}
              className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-off-white hover:text-navy"
            >
              <Users size={17} />
              Info
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void onToggleMute(thread);
            }}
            className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:bg-off-white hover:text-navy"
          >
            {thread.is_muted ? <Bell size={17} /> : <BellOff size={17} />}
            {thread.is_muted ? 'Unmute notifications' : 'Mute notifications'}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void onRemove(thread);
            }}
            className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-red-600 hover:bg-red-50"
          >
            {thread.kind === 'group' ? <LogOut size={17} /> : <Archive size={17} />}
            {removeLabel}
          </button>
        </div>
      )}
    </div>
  );
}

// Compact recency label for inbox rows: time today, "Yesterday", weekday
// within the last week, otherwise a short date.
function formatThreadTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  if (now.getTime() - date.getTime() < 6 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

function formatDaySeparator(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

function extractApiError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      const candidate =
        record.non_field_errors ?? record.error ?? record.detail ?? Object.values(record)[0];
      if (Array.isArray(candidate)) return String(candidate[0]);
      if (candidate) return String(candidate);
    }
  }
  return fallback;
}

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  // Keyed by thread so pictures picked in one chat never send to another.
  const [pendingPictures, setPendingPictures] = useState<{ threadId: number | null; files: File[] }>({
    threadId: null,
    files: [],
  });
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Last rendered message id, so polling refreshes don't re-trigger autoscroll.
  const lastMessageIdRef = useRef<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Tracks the ?thread=<id> we've already tried to fetch so a missing thread
  // triggers at most one extra refresh (no fetch loop).
  const deepLinkFetchedRef = useRef<number | null>(null);
  const recipientDeepLinkRef = useRef<number | null>(null);
  // Last thread we told the nav badge about, so opening a chat pings Layout
  // once instead of on every 5s message poll.
  const badgeSyncedThreadRef = useRef<number | null>(null);
  
  // User directory state
  const [showUserDirectory, setShowUserDirectory] = useState(false);
  const [allUsers, setAllUsers] = useState<UserListItem[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // New-message composer: direct (1:1) vs group, plus which finder tab is
  // active. Directory (name search) is the default; Jarvis is one tab away.
  const [composerMode, setComposerMode] = useState<'direct' | 'group'>('direct');
  const [activeNewMessageTab, setActiveNewMessageTab] = useState<'directory' | 'jarvis' | 'forum'>('directory');

  // Jarvis find recipient state
  const [jarvisMessages, setJarvisMessages] = useState<ChatMessage[]>([]);
  const [jarvisInput, setJarvisInput] = useState('');
  const [jarvisLoading, setJarvisLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const jarvisChatEndRef = useRef<HTMLDivElement>(null);
  const jarvisInputRef = useRef<HTMLInputElement>(null);

  // Forum roster state (shared by the "By Forum" tab and group creation).
  const [myForums, setMyForums] = useState<ForumListItem[]>([]);
  const [forumsLoaded, setForumsLoaded] = useState(false);
  const [loadingForums, setLoadingForums] = useState(false);
  const [forumRoster, setForumRoster] = useState<Record<number, ForumUser[]>>({});
  const [loadingRosterId, setLoadingRosterId] = useState<number | null>(null);
  const [directForumId, setDirectForumId] = useState<number | null>(null);

  // Group creation state
  const [groupForumId, setGroupForumId] = useState<number | null>(null);
  const [groupSelectedIds, setGroupSelectedIds] = useState<number[]>([]);
  const [groupName, setGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [composerError, setComposerError] = useState('');

  // Draft 1:1: a chosen recipient with no thread yet. The thread is only
  // created when the first real message is sent (iMessage-style; no placeholder).
  const [draftRecipient, setDraftRecipient] = useState<Recipient | null>(null);
  const [sendError, setSendError] = useState('');
  const limitedPartners = limitedAccessPartners(selectedThread, draftRecipient, user?.id);
  const isDirectChat =
    Boolean(draftRecipient) ||
    (selectedThread != null &&
      (selectedThread.kind ?? selectedThread.thread_type) === 'direct');
  const directPartnerLimited = isDirectChat && limitedPartners.length > 0;

  // Messaging config (group cap, exemption) is read from the server so the UI
  // never drifts from backend enforcement.
  const [groupMaxMembers, setGroupMaxMembers] = useState(DEFAULT_GROUP_MAX_MEMBERS);
  const [isCapExempt, setIsCapExempt] = useState(isLeadershipRole(user?.role));
  const [canCreateOrgGroup, setCanCreateOrgGroup] = useState(isAdminRole(user?.role));
  const [addParticipantSearch, setAddParticipantSearch] = useState('');
  const [groupRoleFilter, setGroupRoleFilter] = useState('');
  const [groupForumFilter, setGroupForumFilter] = useState('');

  // Message edit/delete state.
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');

  // Add-participant (group threads) state.
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [infoThread, setInfoThread] = useState<Thread | null>(null);
  const [addParticipantRoster, setAddParticipantRoster] = useState<UserListItem[]>([]);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [managingThreadId, setManagingThreadId] = useState<number | null>(null);
  const [managementError, setManagementError] = useState('');

  const {
    error: threadsError,
    isInitialLoading: threadsInitialLoading,
    refresh: refreshThreads,
  } = useVisibilityPolling({
    fetcher: messagingAPI.getThreads,
    onSuccess: (data) => {
      // The open thread is being read continuously (its messages poll advances
      // the read pointer), so never let a concurrently computed snapshot
      // resurrect its unread badge.
      setThreads(
        data.map((thread) =>
          thread.id === selectedThread?.id && thread.unread_count > 0
            ? { ...thread, unread_count: 0 }
            : thread,
        ),
      );
      setSelectedThread((current) => {
        if (!current) return current;
        return data.find((thread) => thread.id === current.id) ?? current;
      });
    },
    intervalMs: 7000,
  });

  const selectedThreadId = selectedThread?.id ?? null;
  const pendingImages =
    selectedThreadId !== null && pendingPictures.threadId === selectedThreadId
      ? pendingPictures.files
      : [];
  const setPendingImages = (files: File[]) => setPendingPictures({ threadId: selectedThreadId, files });
  const chatOpen = Boolean(selectedThread || draftRecipient);
  const keyboardInset = useKeyboardInset(chatOpen);

  useEffect(() => {
    setActiveChatThreadId(selectedThreadId);
    return () => setActiveChatThreadId(null);
  }, [selectedThreadId]);

  useEffect(() => {
    if (keyboardInset <= 0) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [keyboardInset]);

  const {
    error: messagesError,
    isInitialLoading: messagesInitialLoading,
    refresh: refreshMessages,
  } = useVisibilityPolling({
    fetcher: () => messagingAPI.getMessages(selectedThreadId as number),
    onSuccess: (data) => {
      setMessages((current) => {
        const merged = new Map(current.map((message) => [message.id, message]));
        data.forEach((message) => merged.set(message.id, message));
        return [...merged.values()].sort(
          (left, right) =>
            new Date(left.created_at).getTime() - new Date(right.created_at).getTime(),
        );
      });
      // The fetch above marked the thread read server-side; sync the sidebar
      // count and tell the nav badge to refetch now rather than on its slow poll.
      const hadUnread = threads.some(
        (thread) => thread.id === selectedThreadId && thread.unread_count > 0,
      );
      if (hadUnread) {
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === selectedThreadId ? { ...thread, unread_count: 0 } : thread,
          ),
        );
      }
      if (hadUnread || badgeSyncedThreadRef.current !== selectedThreadId) {
        badgeSyncedThreadRef.current = selectedThreadId;
        notifyBadgesStale();
      }
    },
    intervalMs: 5000,
    enabled: selectedThreadId !== null,
    refreshKey: selectedThreadId,
  });

  useEffect(() => {
    messagingAPI
      .getConfig()
      .then((cfg) => {
        setGroupMaxMembers(cfg.group_max_members);
        setIsCapExempt(cfg.is_cap_exempt);
        setCanCreateOrgGroup(cfg.can_create_org_group);
      })
      .catch(() => {});
  }, []);

  // Deep-link support: ?thread=<id> opens that conversation directly (used when
  // Jarvis creates a group chat or starts a 1:1 and navigates here).
  useEffect(() => {
    const threadParam = searchParams.get('thread');
    if (!threadParam) return;
    const id = Number(threadParam);
    if (Number.isNaN(id)) return;
    const match = threads.find((t) => t.id === id);
    if (match) {
      setSelectedThread(match);
    } else if (!threadsInitialLoading && deepLinkFetchedRef.current !== id) {
      deepLinkFetchedRef.current = id;
      void refreshThreads(true);
    }
  }, [
    searchParams,
    threads,
    threadsInitialLoading,
    refreshThreads,
  ]);

  useEffect(() => {
    if (selectedThreadId === null) return;
    // Drop the previous thread's messages immediately so switching never shows
    // stale bubbles (or a false "No messages yet") under the new header.
    setMessages([]);
  }, [selectedThreadId]);

  // Autoscroll only when a genuinely new message appears (not on every poll
  // refresh), and only if the user is already near the bottom — so reading
  // older history isn't interrupted. Opening a thread jumps straight down.
  useEffect(() => {
    const last = messages.length > 0 ? messages[messages.length - 1] : null;
    const previousId = lastMessageIdRef.current;
    lastMessageIdRef.current = last?.id ?? null;
    if (!last || last.id === previousId) return;

    const container = messagesContainerRef.current;
    const nearBottom =
      !container ||
      container.scrollHeight - container.scrollTop - container.clientHeight < 160;
    const isOwnMessage = last.sender.id === user?.id;
    if (previousId === null) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    } else if (nearBottom || isOwnMessage) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, user?.id]);

  // Clear transient per-thread UI state when switching conversations.
  useEffect(() => {
    setEditingMessageId(null);
    setEditingContent('');
    setShowAddParticipant(false);
    setAddParticipantRoster([]);
    setAddParticipantSearch('');
  }, [selectedThread?.id]);

  // Load user directory when modal opens
  useEffect(() => {
    if (showUserDirectory && allUsers.length === 0) {
      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const users = await usersAPI.getDirectory();
          setAllUsers(users);
        } catch (error) {
          console.error('Error fetching users:', error);
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [showUserDirectory, allUsers.length]);

  // Load the user's forums when the group composer or the "By Forum" tab needs them.
  useEffect(() => {
    const needsForums =
      (showUserDirectory &&
        (composerMode === 'group' ||
          (composerMode === 'direct' && activeNewMessageTab === 'forum'))) ||
      (showAddParticipant && canCreateOrgGroup);
    if (!needsForums || forumsLoaded || loadingForums) return;
    const fetchForums = async () => {
      setLoadingForums(true);
      try {
        const forums =
          isAdminRole(user?.role) ? await forumsAPI.getAll() : await forumsAPI.getMyForums();
        setMyForums(forums);
        setForumsLoaded(true);
      } catch (error) {
        console.error('Error fetching forums:', error);
      } finally {
        setLoadingForums(false);
      }
    };
    fetchForums();
  }, [showUserDirectory, showAddParticipant, canCreateOrgGroup, composerMode, activeNewMessageTab, forumsLoaded, loadingForums, user?.role]);

  // Load Jarvis greeting when switching to Jarvis tab
  useEffect(() => {
    if (showUserDirectory && activeNewMessageTab === 'jarvis' && jarvisMessages.length === 0) {
      const fetchGreeting = async () => {
        setJarvisLoading(true);
        try {
          const data = await jarvisAPI.getFindRecipientGreeting();
          setJarvisMessages([{ role: 'assistant', content: data.greeting }]);
        } catch (error) {
          console.error('Error fetching Jarvis greeting:', error);
          setJarvisMessages([{ role: 'assistant', content: "Hey! Who would you like to message? You can search by name, industry, location, or expertise." }]);
        } finally {
          setJarvisLoading(false);
        }
      };
      fetchGreeting();
    }
  }, [showUserDirectory, activeNewMessageTab, jarvisMessages.length]);

  // Scroll Jarvis chat to bottom when new messages arrive
  useEffect(() => {
    jarvisChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [jarvisMessages]);

  // Reset modal state when closing
  const handleCloseModal = () => {
    setShowUserDirectory(false);
    setComposerMode('direct');
    setActiveNewMessageTab('directory');
    setJarvisMessages([]);
    setJarvisInput('');
    setSelectedUser(null);
    setDirectForumId(null);
    setGroupForumId(null);
    setGroupSelectedIds([]);
    setGroupName('');
    setComposerError('');
    setGroupRoleFilter('');
    setGroupForumFilter('');
    setUserSearch('');
  };

  useEffect(() => {
    if (!showUserDirectory) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleCloseModal();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [showUserDirectory]);

  const loadForumRoster = async (forumId: number) => {
    if (forumRoster[forumId] || loadingRosterId === forumId) return;
    setLoadingRosterId(forumId);
    try {
      const detail = await forumsAPI.getById(forumId);
      const seen = new Set<number>();
      const roster: ForumUser[] = [];
      [...(detail.facilitators || []), ...(detail.members || [])].forEach((u) => {
        if (!seen.has(u.id)) {
          seen.add(u.id);
          roster.push(u);
        }
      });
      setForumRoster((prev) => ({ ...prev, [forumId]: roster }));
    } catch (error) {
      console.error('Error fetching forum roster:', error);
      setComposerError(extractApiError(error, 'Could not load this Forum’s members.'));
    } finally {
      setLoadingRosterId(null);
    }
  };

  const selectDirectForum = (forumId: number) => {
    setDirectForumId(forumId);
    loadForumRoster(forumId);
  };

  const selectGroupForum = (forumId: number) => {
    setComposerError('');
    setGroupForumId(forumId);
    setGroupSelectedIds([]);
    loadForumRoster(forumId);
  };

  const toggleGroupMember = (memberId: number) => {
    setComposerError('');
    setGroupSelectedIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const addPendingImages = (files: File[]) => {
    setPendingImages(acceptScreenshots(pendingImages, files, MAX_MESSAGE_PICTURES, 'pictures'));
  };

  const insertEmoji = (emoji: string) => {
    const field = composerRef.current;
    const start = field?.selectionStart ?? newMessage.length;
    const end = field?.selectionEnd ?? newMessage.length;
    setNewMessage(newMessage.slice(0, start) + emoji + newMessage.slice(end));
    requestAnimationFrame(() => {
      field?.focus();
      field?.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  const handleToggleReaction = async (messageId: number, emoji: string) => {
    try {
      const updated = await messagingAPI.react(messageId, emoji);
      setMessages((prev) => prev.map((message) => (message.id === updated.id ? updated : message)));
    } catch (error) {
      toast.error(extractApiError(error, 'Could not add your reaction.'));
    }
  };

  const handleSend = async () => {
    const content = newMessage.trim();
    const images = pendingImages;
    if ((!content && images.length === 0) || isSending || (!selectedThread && !draftRecipient)) return;
    if (directPartnerLimited) return;

    setIsSending(true);
    setSendError('');
    try {
      if (selectedThread) {
        const message = await messagingAPI.sendToThread(selectedThread.id, content, images);
        setMessages((prev) => [...prev, message]);
        setNewMessage('');
        setPendingImages([]);
      } else if (draftRecipient) {
        // First real message creates/opens the 1:1 thread (no placeholder post).
        const result = await messagingAPI.quickSend(draftRecipient.id, content);
        setNewMessage('');
        const updatedThreads = await messagingAPI.getThreads();
        setThreads(updatedThreads);
        const newThread = updatedThreads.find((t) => t.id === result.thread_id);
        if (newThread) {
          setSelectedThread(newThread);
          setDraftRecipient(null);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSendError(extractApiError(error, 'Could not send your message. Please try again.'));
    } finally {
      setIsSending(false);
    }
  };

  // Handle sending message to Jarvis for finding recipient
  const handleJarvisSend = async () => {
    if (!jarvisInput.trim() || jarvisLoading) return;

    const userMessage = jarvisInput.trim();
    setJarvisInput('');
    setJarvisMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setJarvisLoading(true);

    try {
      const conversationHistory = jarvisMessages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const result = await jarvisAPI.findRecipient(userMessage, conversationHistory);
      
      setJarvisMessages(prev => [...prev, {
        role: 'assistant',
        content: result.response,
        members: membersFromAskIfAction(result.action),
      }]);
    } catch (error) {
      console.error('Error chatting with Jarvis:', error);
      setJarvisMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I encountered an issue. Please try again or use the Search Directory tab." 
      }]);
    } finally {
      setJarvisLoading(false);
      // Refocus input so user can keep typing (delay allows React to re-enable the input first)
      setTimeout(() => jarvisInputRef.current?.focus(), 100);
    }
  };

  const handleStartConversation = (recipient: Recipient) => {
    setSendError('');
    setNewMessage('');
    // Reuse an existing 1:1 thread if there is one.
    const existingThread = threads.find(
      (t) =>
        t.thread_type === 'direct' &&
        t.participants.length === 2 &&
        t.participants.some((p) => p.id === recipient.id)
    );

    if (existingThread) {
      setSelectedThread(existingThread);
      setDraftRecipient(null);
    } else {
      // Open an empty draft conversation; nothing is posted until the user
      // sends a real first message.
      setSelectedThread(null);
      setMessages([]);
      setDraftRecipient(recipient);
    }
    handleCloseModal();
  };

  // Deep-link into the composer (e.g. /messages?compose=group from the
  // Accountability Groups page) so group chats are reachable from where
  // people expect them, not just the new-message modal.
  useEffect(() => {
    const compose = searchParams.get('compose');
    if (compose !== 'group' && compose !== 'direct') return;
    setComposerMode(compose);
    setShowUserDirectory(true);
    // One-shot: clear the param so closing the composer doesn't reopen it.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('compose');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Deep-link support for facilitator/member actions. Resolve the recipient
  // through the visibility-aware directory and open either the existing 1:1
  // thread or an unsent draft conversation.
  useEffect(() => {
    const raw = searchParams.get('recipient') || searchParams.get('to');
    const recipientId = Number(raw);
    if (
      !raw ||
      Number.isNaN(recipientId) ||
      recipientDeepLinkRef.current === recipientId ||
      threadsInitialLoading
    )
      return;
    recipientDeepLinkRef.current = recipientId;
    const existingThread = threads.find(
      (thread) =>
        thread.thread_type === 'direct' &&
        thread.participants.length === 2 &&
        thread.participants.some((participant) => participant.id === recipientId),
    );
    if (existingThread) {
      setSelectedThread(existingThread);
      setDraftRecipient(null);
      return;
    }
    usersAPI
      .getDirectory()
      .then((directory) => {
        const recipient = directory.find((candidate) => candidate.id === recipientId);
        if (!recipient) {
          toast.error('That member is not available for messaging');
          return;
        }
        setSelectedThread(null);
        setMessages([]);
        setDraftRecipient(recipient);
      })
      .catch(() => toast.error('Could not open this conversation'));
  }, [searchParams, threads, threadsInitialLoading]);

  const handleCreateGroup = async () => {
    if (groupSelectedIds.length === 0 || creatingGroup) return;
    if (!canCreateOrgGroup && !groupForumId) return;
    setCreatingGroup(true);
    setComposerError('');
    try {
      const existingIds = new Set(threads.map((t) => t.id));
      await messagingAPI.createThread({
        name: groupName.trim(),
        thread_type: 'group',
        kind: 'group',
        forum: canCreateOrgGroup ? null : groupForumId,
        participant_ids: groupSelectedIds,
      });
      const updatedThreads = await messagingAPI.getThreads();
      setThreads(updatedThreads);
      const created =
        updatedThreads.find((t) => !existingIds.has(t.id) && t.thread_type === 'group') ??
        updatedThreads.find(
          (t) => t.thread_type === 'group' && t.forum === groupForumId
        );
      if (created) {
        setSelectedThread(created);
        setDraftRecipient(null);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error creating group chat:', error);
      setComposerError(extractApiError(error, 'Could not create the group chat.'));
    } finally {
      setCreatingGroup(false);
    }
  };

  const startEditMessage = (message: Message) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async (messageId: number) => {
    const content = editingContent.trim();
    if (!content) return;
    try {
      const updated = await messagingAPI.updateMessage(messageId, content);
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, ...updated } : m)));
      cancelEditMessage();
    } catch (error) {
      console.error('Error editing message:', error);
      setSendError(extractApiError(error, 'Could not edit the message.'));
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      await messagingAPI.deleteMessage(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      setSendError(extractApiError(error, 'Could not delete the message.'));
    }
  };

  const handleToggleMute = async (thread: Thread) => {
    setManagingThreadId(thread.id);
    setManagementError('');
    try {
      const result = await messagingAPI.setMuted(thread.id, !thread.is_muted);
      setThreads((current) =>
        current.map((item) =>
          item.id === thread.id ? { ...item, is_muted: result.is_muted } : item
        )
      );
      setSelectedThread((current) =>
        current?.id === thread.id ? { ...current, is_muted: result.is_muted } : current
      );
    } catch (error) {
      setManagementError(extractApiError(error, 'Could not update notification settings.'));
    } finally {
      setManagingThreadId(null);
    }
  };

  const handleRemoveConversation = async (thread: Thread) => {
    const isGroup = thread.kind === 'group';
    const confirmed = window.confirm(
      isGroup
        ? 'Leave this conversation? You will no longer receive new messages from this group.'
        : 'Remove this conversation from your inbox? It will return if someone sends a new message.'
    );
    if (!confirmed) return;

    setManagingThreadId(thread.id);
    setManagementError('');
    try {
      await messagingAPI.removeThread(thread.id);
      setThreads((current) => current.filter((item) => item.id !== thread.id));
      if (selectedThread?.id === thread.id) {
        setSelectedThread(null);
        setMessages([]);
      }
    } catch (error) {
      setManagementError(extractApiError(error, 'Could not remove the conversation.'));
    } finally {
      setManagingThreadId(null);
    }
  };

  // Open the add-participant picker for the current group thread.
  const openAddParticipant = async () => {
    if (!selectedThread || selectedThread.kind !== 'group') return;
    setShowAddParticipant(true);
    setAddParticipantSearch('');
    try {
      if (!selectedThread.forum) {
        const directory = allUsers.length > 0 ? allUsers : await usersAPI.getDirectory();
        if (allUsers.length === 0) setAllUsers(directory);
        setAddParticipantRoster(directory);
        return;
      }
      const detail = await forumsAPI.getById(selectedThread.forum);
      const seen = new Set<number>();
      const roster: UserListItem[] = [];
      [...(detail.facilitators || []), ...(detail.members || [])].forEach((u) => {
        if (!seen.has(u.id)) {
          seen.add(u.id);
          roster.push({
            id: u.id,
            full_name: u.full_name || u.username,
            role: u.role,
            chapter: u.chapter,
            avatar_url: u.avatar_url,
            is_paused: u.is_paused,
            limit_app_access: u.limit_app_access,
          });
        }
      });
      setAddParticipantRoster(roster);
    } catch (error) {
      console.error('Error loading people to add:', error);
    }
  };

  const handleAddParticipant = async (userId: number) => {
    if (!selectedThread || addingParticipant) return;
    setAddingParticipant(true);
    try {
      await messagingAPI.addParticipant(selectedThread.id, userId);
      const updatedThreads = await messagingAPI.getThreads();
      setThreads(updatedThreads);
      const refreshed = updatedThreads.find((t) => t.id === selectedThread.id);
      if (refreshed) setSelectedThread(refreshed);
      setShowAddParticipant(false);
    } catch (error) {
      console.error('Error adding participant:', error);
      setSendError(extractApiError(error, 'Could not add that person to the chat.'));
    } finally {
      setAddingParticipant(false);
    }
  };

  const isForumDefault = (thread: Thread) =>
    thread.kind === 'forum_default';

  const getThreadName = (thread: Thread) => {
    if (thread.name) return thread.name;
    const otherParticipants = thread.participants.filter(p => p.id !== user?.id);
    return otherParticipants.map(p => p.full_name || p.username).join(', ');
  };

  const filteredUsers = allUsers.filter(
    (u) =>
      u.full_name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.role.toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.chapter && u.chapter.toLowerCase().includes(userSearch.toLowerCase()))
  );
  const showOrgGroupBuilder = canCreateOrgGroup;
  const showGroupForumPicker = !showOrgGroupBuilder && groupForumId === null;
  const groupCandidateUsers = showOrgGroupBuilder
    ? filteredUsers.filter((u) =>
        u.id !== user?.id
        && matchesRoleFilter(u.role, groupRoleFilter)
        && matchesForumFilter(u.forums, groupForumFilter)
      )
    : (groupForumId ? forumRoster[groupForumId] || [] : []).filter((u) => u.id !== user?.id);
  const addCandidateUsers = addParticipantRoster.filter(
    (u) =>
      !selectedThread?.participants.some((p) => p.id === u.id) &&
      (!addParticipantSearch ||
        u.full_name.toLowerCase().includes(addParticipantSearch.toLowerCase())) &&
      (selectedThread?.forum ||
        (matchesRoleFilter(u.role, groupRoleFilter) && matchesForumFilter(u.forums, groupForumFilter)))
  );
  const groupCandidatesLoading = showOrgGroupBuilder
    ? loadingUsers
    : Boolean(groupForumId && (loadingRosterId === groupForumId || !forumRoster[groupForumId]));

  if (threadsInitialLoading) {
    return (
      <div className="grid h-full min-h-0 flex-1 grid-cols-1 overflow-hidden rounded-2xl bg-white shadow-sm md:h-[calc(100vh-8rem-env(safe-area-inset-top))] md:grid-cols-[20rem_1fr]" role="status" aria-label="Loading messages">
        <div className="space-y-3 border-r border-gray-100 p-4">
          <div className="h-8 w-32 animate-pulse rounded bg-gray-200" />
          {[0, 1, 2, 3].map((item) => <div key={item} className="h-16 animate-pulse rounded-xl bg-gray-100" />)}
        </div>
        <div className="hidden animate-pulse bg-gray-50 md:block" />
        <span className="sr-only">Loading conversations…</span>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-1 overflow-hidden rounded-2xl bg-white shadow-sm md:h-[calc(100vh-8rem-env(safe-area-inset-top))]">
      {/* User Directory Modal */}
      {showUserDirectory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) handleCloseModal();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-message-title"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl h-[min(44rem,calc(100dvh-1.5rem))] sm:h-[min(44rem,calc(100dvh-3rem))] flex flex-col overflow-hidden"
          >
            {/* Modal Header */}
            <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-gray-100 flex items-center justify-between">
              <h2 id="new-message-title" className="text-xl font-bold text-navy">
                New Message
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                aria-label="Close new message dialog"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Mode toggle: 1:1 vs group */}
            <div className="px-4 pt-4 sm:px-6">
              <div className="flex gap-1 bg-off-white rounded-xl p-1.5">
                <button
                  onClick={() => {
                    setComposerMode('direct');
                    setComposerError('');
                  }}
                  className={`flex-1 min-h-11 px-3 py-2 text-sm sm:text-base font-medium rounded-lg transition flex items-center justify-center gap-2 ${
                    composerMode === 'direct'
                      ? 'bg-white text-navy shadow-sm'
                      : 'text-gray-500 hover:text-navy'
                  }`}
                >
                  <User size={16} />
                  Direct
                </button>
                <button
                  onClick={() => {
                    setComposerMode('group');
                    setSelectedUser(null);
                    setComposerError('');
                  }}
                  className={`flex-1 min-h-11 px-3 py-2 text-sm sm:text-base font-medium rounded-lg transition flex items-center justify-center gap-2 ${
                    composerMode === 'group'
                      ? 'bg-white text-navy shadow-sm'
                      : 'text-gray-500 hover:text-navy'
                  }`}
                >
                  <Users size={16} />
                  Group Chat
                </button>
              </div>
            </div>

            {/* Direct-message finder tabs */}
            {composerMode === 'direct' && (
              <div className="flex overflow-x-auto border-b border-gray-100 mt-3 px-1 sm:px-4" role="tablist" aria-label="Find a recipient">
                <button
                  role="tab"
                  aria-selected={activeNewMessageTab === 'directory'}
                  onClick={() => setActiveNewMessageTab('directory')}
                  className={`flex-1 min-w-[6.5rem] px-3 py-3.5 text-sm sm:text-base font-medium whitespace-nowrap transition flex items-center justify-center gap-1.5 ${
                    activeNewMessageTab === 'directory'
                      ? 'text-navy border-b-2 border-navy'
                      : 'text-gray-500 hover:text-navy'
                  }`}
                >
                  <Search size={16} />
                  Directory
                </button>
                <button
                  role="tab"
                  aria-selected={activeNewMessageTab === 'jarvis'}
                  onClick={() => setActiveNewMessageTab('jarvis')}
                  className={`flex-1 min-w-[6.5rem] px-3 py-3.5 text-sm sm:text-base font-medium whitespace-nowrap transition flex items-center justify-center gap-1.5 ${
                    activeNewMessageTab === 'jarvis'
                      ? 'text-navy border-b-2 border-navy'
                      : 'text-gray-500 hover:text-navy'
                  }`}
                >
                  <Sparkles size={16} />
                  Ask IF
                </button>
                <button
                  role="tab"
                  aria-selected={activeNewMessageTab === 'forum'}
                  onClick={() => setActiveNewMessageTab('forum')}
                  className={`flex-1 min-w-[6.5rem] px-3 py-3.5 text-sm sm:text-base font-medium whitespace-nowrap transition flex items-center justify-center gap-1.5 ${
                    activeNewMessageTab === 'forum'
                      ? 'text-navy border-b-2 border-navy'
                      : 'text-gray-500 hover:text-navy'
                  }`}
                >
                  <Building2 size={16} />
                  By Forum
                </button>
              </div>
            )}

            {composerError && composerMode === 'direct' && (
              <div className="mx-4 mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                {composerError}
              </div>
            )}

            {/* User Confirmation Panel */}
            {composerMode === 'direct' && selectedUser && (
              <div className="p-4 bg-off-white border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                    {selectedUser.avatar_url ? (
                      <img
                        src={selectedUser.avatar_url}
                        alt={selectedUser.full_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold text-lg">
                        {selectedUser.full_name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy">
                      {selectedUser.full_name}
                      <AccessBadges person={selectedUser} className="ml-2 align-middle" />
                    </p>
                    {selectedUser.limit_app_access && (
                      <p className="text-xs text-amber-800 mt-0.5">
                        They can’t see messages while access is limited.
                      </p>
                    )}
                    <p className="text-sm text-gray-600">
                      {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                      {selectedUser.chapter && ` • ${selectedUser.chapter}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleStartConversation(selectedUser)}
                    className="flex-1 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition flex items-center justify-center gap-2"
                  >
                    <Check size={16} />
                    Start Conversation
                  </button>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition flex items-center justify-center gap-2"
                  >
                    <ArrowLeft size={16} />
                    Back
                  </button>
                </div>
              </div>
            )}
            
            {/* Directory Tab Content */}
            {composerMode === 'direct' && activeNewMessageTab === 'directory' && !selectedUser && (
              <>
                {/* Search */}
                <div className="p-4 border-b border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search by name, role, or chapter..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                    />
                  </div>
                </div>
                
                {/* User List */}
                <div className="flex-1 overflow-y-auto">
                  {loadingUsers ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto"></div>
                      <p className="text-gray-500 mt-2">Loading users...</p>
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => handleStartConversation(u)}
                        className="w-full p-4 flex items-center gap-3 hover:bg-off-white transition text-left border-b border-gray-50"
                      >
                        <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                          {u.avatar_url ? (
                            <img
                              src={u.avatar_url}
                              alt={u.full_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-bold">
                              {u.full_name.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-navy">
                            {u.full_name}
                            <AccessBadges person={u} className="ml-2 align-middle" />
                          </p>
                          <p className="text-sm text-gray-500">
                            {u.limit_app_access
                              ? 'Can’t see messages while access is limited'
                              : `${u.role.charAt(0).toUpperCase() + u.role.slice(1)}${u.chapter ? ` • ${u.chapter}` : ''}`}
                          </p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <Users className="mx-auto text-gray-300" size={48} />
                      <p className="text-gray-500 mt-2">No users found</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Jarvis Tab Content */}
            {composerMode === 'direct' && activeNewMessageTab === 'jarvis' && !selectedUser && (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
                  {jarvisMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 bg-gradient-to-br from-navy to-gold rounded-full flex items-center justify-center flex-shrink-0">
                          <Sparkles className="text-white" size={14} />
                        </div>
                      )}
                      <div
                        className={`max-w-[88%] sm:max-w-[78%] px-4 py-3.5 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-navy text-white rounded-br-md'
                            : 'bg-off-white text-gray-800 rounded-bl-md'
                        }`}
                      >
                        <p className="whitespace-pre-wrap text-[15px] leading-6">{msg.content}</p>
                        {msg.role === 'assistant' && msg.members && msg.members.length > 0 && (
                          <AskIfMemberCards
                            members={msg.members}
                            onPick={(member) => setSelectedUser({
                              id: member.id,
                              full_name: member.full_name || member.name || '',
                              role: member.role || '',
                              chapter: member.chapter || '',
                              avatar_url: member.avatar_url,
                              industry: member.industry || '',
                              bio: member.bio || '',
                              areas_of_expertise: member.areas_of_expertise || [],
                              profile_completed: true,
                            })}
                            pickLabel="Message"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {jarvisLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-navy to-gold rounded-full flex items-center justify-center flex-shrink-0">
                        <Sparkles className="text-white" size={14} />
                      </div>
                      <div className="bg-off-white p-3 rounded-2xl rounded-bl-md">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={jarvisChatEndRef} />
                </div>
                
                {/* Input */}
                <div className="p-3 sm:p-5 border-t border-gray-100 bg-white">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="relative flex-1">
                      <input
                        ref={jarvisInputRef}
                        type="text"
                        value={jarvisInput}
                        onChange={(e) => setJarvisInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleJarvisSend()}
                        placeholder="Describe who you're looking for..."
                        className="w-full min-h-12 px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-base"
                        disabled={jarvisLoading}
                      />
                      <MicButton
                        value={jarvisInput}
                        onChange={setJarvisInput}
                        className="absolute top-1/2 -translate-y-1/2 right-1.5"
                      />
                    </div>
                    <button
                      onClick={handleJarvisSend}
                      disabled={!jarvisInput.trim() || jarvisLoading}
                      className="w-12 h-12 flex items-center justify-center bg-navy text-white rounded-xl hover:bg-navy-dark transition disabled:opacity-50 flex-shrink-0"
                      aria-label="Send message"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* By Forum Tab Content */}
            {composerMode === 'direct' && activeNewMessageTab === 'forum' && !selectedUser && (
              <div className="flex-1 flex flex-col overflow-hidden">
                {directForumId !== null && (
                  <div className="p-3 border-b border-gray-100">
                    <button
                      onClick={() => setDirectForumId(null)}
                      className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-navy transition"
                    >
                      <ArrowLeft size={16} />
                      All forums
                    </button>
                  </div>
                )}
                <div className="flex-1 overflow-y-auto">
                  {loadingForums ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto"></div>
                      <p className="text-gray-500 mt-2">Loading Forums...</p>
                    </div>
                  ) : directForumId === null ? (
                    myForums.length > 0 ? (
                      myForums.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => selectDirectForum(f.id)}
                          className="w-full p-4 flex items-center gap-3 hover:bg-off-white transition text-left border-b border-gray-50"
                        >
                          <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                            <Building2 className="text-white" size={20} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-navy truncate">{f.name}</p>
                            <p className="text-sm text-gray-500 truncate">
                              {[f.city, f.state].filter(Boolean).join(', ')}
                            </p>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-8 text-center">
                        <Building2 className="mx-auto text-gray-300" size={48} />
                        <p className="text-gray-500 mt-2">You're not in any Forums yet</p>
                      </div>
                    )
                  ) : loadingRosterId === directForumId || !forumRoster[directForumId] ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto"></div>
                      <p className="text-gray-500 mt-2">Loading members...</p>
                    </div>
                  ) : (
                    forumRoster[directForumId]
                      .filter((u) => u.id !== user?.id)
                      .map((u) => (
                        <button
                          key={u.id}
                          onClick={() => handleStartConversation(u)}
                          className="w-full p-4 flex items-center gap-3 hover:bg-off-white transition text-left border-b border-gray-50"
                        >
                          <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                            {u.avatar_url ? (
                              <img
                                src={u.avatar_url}
                                alt={u.full_name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white font-bold">
                                {(u.full_name || u.username).charAt(0)}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-navy">
                              {u.full_name || u.username}
                              <AccessBadges person={u} className="ml-2 align-middle" />
                            </p>
                            <p className="text-sm text-gray-500">
                              {u.limit_app_access
                                ? 'Can’t see messages while access is limited'
                                : `${u.role.charAt(0).toUpperCase() + u.role.slice(1)}${u.chapter ? ` • ${u.chapter}` : ''}`}
                            </p>
                          </div>
                        </button>
                      ))
                  )}
                </div>
              </div>
            )}

            {/* Group Builder */}
            {composerMode === 'group' && (
              <div className="flex-1 flex flex-col overflow-hidden mt-3">
                {composerError && (
                  <div className="mx-4 mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                    {composerError}
                  </div>
                )}

                {showGroupForumPicker ? (
                  <>
                    <p className="px-4 pb-2 text-sm text-gray-500">
                      Pick the forum this group chat belongs to.
                    </p>
                    <div className="flex-1 overflow-y-auto">
                      {loadingForums ? (
                        <div className="p-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto"></div>
                          <p className="text-gray-500 mt-2">Loading Forums...</p>
                        </div>
                      ) : myForums.length > 0 ? (
                        myForums.map((f) => (
                          <button
                            key={f.id}
                            onClick={() => selectGroupForum(f.id)}
                            className="w-full p-4 flex items-center gap-3 hover:bg-off-white transition text-left border-b border-gray-50"
                          >
                            <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                              <Building2 className="text-white" size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-navy truncate">{f.name}</p>
                              <p className="text-sm text-gray-500 truncate">
                                {[f.city, f.state].filter(Boolean).join(', ')}
                              </p>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="p-8 text-center">
                          <Building2 className="mx-auto text-gray-300" size={48} />
                          <p className="text-gray-500 mt-2">You're not in any Forums yet</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-4 pb-3 space-y-3">
                      {!showOrgGroupBuilder && (
                        <button
                          onClick={() => {
                            setGroupForumId(null);
                            setGroupSelectedIds([]);
                            setComposerError('');
                          }}
                          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-navy transition"
                        >
                          <ArrowLeft size={16} />
                          Change forum
                        </button>
                      )}
                      {showOrgGroupBuilder && (
                        <p className="text-sm text-gray-500">
                          Add anyone in Iron Forums. There is no size limit.
                        </p>
                      )}
                      <input
                        type="text"
                        placeholder="Group name (optional)"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                      />
                      {showOrgGroupBuilder && (
                        <>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              placeholder="Search members…"
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="sr-only" htmlFor="group-role-filter">Role</label>
                            <select
                              id="group-role-filter"
                              value={groupRoleFilter}
                              onChange={(e) => setGroupRoleFilter(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-navy bg-white focus:ring-2 focus:ring-navy outline-none"
                            >
                              {GROUP_ROLE_FILTERS.map((option) => (
                                <option key={option.value || 'all'} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <label className="sr-only" htmlFor="group-forum-filter">Forum</label>
                            <select
                              id="group-forum-filter"
                              value={groupForumFilter}
                              onChange={(e) => setGroupForumFilter(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-navy bg-white focus:ring-2 focus:ring-navy outline-none"
                            >
                              <option value="">All forums</option>
                              <option value="none">No forum</option>
                              {myForums.map((forum) => (
                                <option key={forum.id} value={String(forum.id)}>
                                  {forum.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Members</span>
                        <span
                          className={`font-medium ${
                            !showOrgGroupBuilder && !isCapExempt && groupSelectedIds.length + 1 >= groupMaxMembers
                              ? 'text-gold-dark'
                              : 'text-navy'
                          }`}
                        >
                          {groupSelectedIds.length + 1}
                          {showOrgGroupBuilder || isCapExempt ? '' : ` / ${groupMaxMembers}`} members
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto border-t border-gray-100">
                      {groupCandidatesLoading ? (
                        <div className="p-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto"></div>
                          <p className="text-gray-500 mt-2">Loading members...</p>
                        </div>
                      ) : groupCandidateUsers.length === 0 ? (
                        <div className="p-8 text-center">
                          <p className="text-gray-500">No members match those filters.</p>
                        </div>
                      ) : (
                        groupCandidateUsers.map((u) => {
                            const checked = groupSelectedIds.includes(u.id);
                            const atCap =
                              !showOrgGroupBuilder &&
                              !isCapExempt &&
                              groupSelectedIds.length + 1 >= groupMaxMembers;
                            const disabled = !checked && atCap;
                            const displayName = u.full_name || ('username' in u ? String(u.username) : '');
                            return (
                              <button
                                key={u.id}
                                onClick={() => toggleGroupMember(u.id)}
                                disabled={disabled}
                                className={`w-full p-4 flex items-center gap-3 transition text-left border-b border-gray-50 ${
                                  checked ? 'bg-off-white' : 'hover:bg-off-white'
                                } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                              >
                                <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                                  {u.avatar_url ? (
                                    <img
                                      src={u.avatar_url}
                                      alt={displayName}
                                      className="w-10 h-10 rounded-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-white font-bold">
                                      {displayName.charAt(0)}
                                    </span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-navy">
                                    {displayName}
                                    <AccessBadges person={u} className="ml-2 align-middle" />
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {u.limit_app_access
                                      ? 'Can’t see messages while access is limited'
                                      : `${roleLabel(u.role) || u.role}${
                                          showOrgGroupBuilder && 'forums' in u && forumNames(u.forums)
                                            ? ` • ${forumNames(u.forums)}`
                                            : u.chapter
                                              ? ` • ${u.chapter}`
                                              : ''
                                        }`}
                                  </p>
                                </div>
                                <div
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    checked
                                      ? 'bg-navy border-navy'
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {checked && <Check className="text-white" size={14} />}
                                </div>
                              </button>
                            );
                          })
                      )}
                    </div>

                    <div className="p-4 border-t border-gray-100">
                      <button
                        onClick={handleCreateGroup}
                        disabled={groupSelectedIds.length === 0 || creatingGroup}
                        className="w-full px-4 py-2.5 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <UserPlus size={16} />
                        {creatingGroup ? 'Creating…' : 'Create Group Chat'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Thread List */}
      <div
        className={`${
          selectedThread || draftRecipient ? 'hidden md:flex' : 'flex'
        } flex-col w-full md:w-80 border-r border-gray-100`}
      >
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h1 className="text-xl font-bold text-navy">
            Messages
          </h1>
          <button
            onClick={() => setShowUserDirectory(true)}
            className="p-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition"
            title="New Message"
            aria-label="New message"
          >
            <Plus size={20} />
          </button>
        </div>
        
        {managementError && (
          <div role="alert" className="mx-3 mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
            {managementError}
          </div>
        )}

        {Boolean(threadsError) && (
          <div role="alert" className="mx-3 mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>Could not refresh conversations.</span>
            </div>
            <button
              type="button"
              onClick={() => void refreshThreads()}
              className="mt-2 inline-flex items-center gap-1 font-semibold hover:underline"
            >
              <RefreshCw size={13} /> Try again
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {threads.length > 0 ? (
            threads.map((thread) => (
              <div
                key={thread.id}
                className={`w-full px-2 py-1.5 flex items-start gap-1 hover:bg-off-white transition ${
                  selectedThread?.id === thread.id ? 'bg-off-white' : ''
                }`}
              >
                <button
                  type="button"
                  aria-label={`Open ${getThreadName(thread)}`}
                  onClick={() => {
                    setSelectedThread(thread);
                    setDraftRecipient(null);
                    setSendError('');
                    setManagementError('');
                  }}
                  className="flex-1 min-w-0 p-2 flex items-start gap-3 text-left rounded-xl"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isForumDefault(thread)
                      ? 'bg-navy-dark'
                      : thread.thread_type === 'group'
                        ? 'bg-navy'
                        : 'bg-gold'
                  }`}>
                    {isForumDefault(thread) ? (
                      <Building2 className="text-white" size={20} />
                    ) : thread.thread_type === 'group' ? (
                      <Users className="text-white" size={20} />
                    ) : (
                      <User className="text-white" size={20} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-navy truncate">
                        {getThreadName(thread)}
                      </p>
                      {thread.last_message && (
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatThreadTime(thread.last_message.created_at)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isForumDefault(thread) && (
                        <span className="inline-block text-[10px] uppercase tracking-wide font-semibold text-navy-dark bg-white rounded px-1.5 py-0.5 mt-0.5">
                          Forum chat
                        </span>
                      )}
                      {thread.thread_type === 'direct' &&
                        thread.participants.some(
                          (p) => p.id !== user?.id && (p.limit_app_access || p.is_paused),
                        ) && (
                          <AccessBadges
                            person={
                              thread.participants.find((p) => p.id !== user?.id) || {}
                            }
                            className="mt-0.5"
                          />
                        )}
                      {thread.is_muted && (
                        <BellOff size={13} className="text-gray-400 mt-0.5" aria-label="Notifications muted" />
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      {thread.last_message && (
                        <p className="text-sm text-gray-500 truncate">
                          {thread.last_message.content}
                        </p>
                      )}
                      {thread.unread_count > 0 && thread.id !== selectedThread?.id && (
                        <span className="bg-gold text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-auto">
                          {thread.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
                <div className="pt-1">
                  <ConversationMenu
                    thread={thread}
                    onToggleMute={handleToggleMute}
                    onRemove={handleRemoveConversation}
                    onInfo={setInfoThread}
                    disabled={managingThreadId === thread.id}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center">
              <MessageSquare className="mx-auto text-gray-300" size={48} />
              <p className="text-gray-500 mt-2">No conversations yet</p>
              <button
                onClick={() => setShowUserDirectory(true)}
                className="mt-4 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition text-sm"
              >
                Start a Conversation
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Message View */}
      <div
        className={`${
          selectedThread || draftRecipient ? 'flex' : 'hidden md:flex'
        } min-h-0 min-w-0 flex-1 flex-col${chatOpen ? ' chat-keyboard-lift' : ''}`}
      >
        {selectedThread || draftRecipient ? (
          <>
            {/* Thread Header */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <button
                onClick={() => {
                  setSelectedThread(null);
                  setDraftRecipient(null);
                }}
                className="md:hidden p-2 hover:bg-off-white rounded-lg"
              >
                <ChevronLeft size={20} />
              </button>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                selectedThread && isForumDefault(selectedThread)
                  ? 'bg-navy-dark'
                  : selectedThread?.thread_type === 'group'
                    ? 'bg-navy'
                    : 'bg-gold'
              }`}>
                {selectedThread && isForumDefault(selectedThread) ? (
                  <Building2 className="text-white" size={20} />
                ) : selectedThread?.thread_type === 'group' ? (
                  <Users className="text-white" size={20} />
                ) : (
                  <User className="text-white" size={20} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                {/* 1:1 conversations: the name links to the member's profile */}
                {(() => {
                  const dmPartnerId =
                    selectedThread && (selectedThread.kind ?? selectedThread.thread_type) === 'direct'
                      ? selectedThread.participants.find((p) => p.id !== user?.id)?.id
                      : draftRecipient?.id;
                  const title = selectedThread ? getThreadName(selectedThread) : draftRecipient?.full_name;
                  return (
                    <div className="flex items-center gap-2 min-w-0">
                      {dmPartnerId ? (
                        <Link
                          to={`/members/${dmPartnerId}`}
                          className="font-semibold text-navy truncate hover:underline"
                          title="View profile"
                        >
                          {title}
                        </Link>
                      ) : (
                        <p className="font-semibold text-navy truncate">{title}</p>
                      )}
                      {draftRecipient ? (
                        <AccessBadges person={draftRecipient} className="flex-shrink-0" />
                      ) : (
                        selectedThread && (
                          <AccessBadges
                            person={
                              selectedThread.participants.find((p) => p.id !== user?.id) || {}
                            }
                            className="flex-shrink-0"
                          />
                        )
                      )}
                    </div>
                  );
                })()}
                <p className="text-xs text-gray-500">
                  {selectedThread
                    ? isForumDefault(selectedThread)
                      ? `Forum chat • ${selectedThread.participants.length} members`
                      : `${selectedThread.participants.length} participants`
                    : 'New conversation'}
                </p>
              </div>
              {/* Add participant: ad-hoc group threads only (not forum-default,
                  which auto-syncs to the roster, and not 1:1 DMs). */}
              {selectedThread &&
                selectedThread.kind === 'group' &&
                (selectedThread.forum || canCreateOrgGroup) && (
                  <button
                    onClick={openAddParticipant}
                    className="p-2 hover:bg-off-white rounded-lg text-navy transition flex-shrink-0"
                    title="Add participant"
                    aria-label="Add participant"
                  >
                    <UserPlus size={20} />
                  </button>
                )}
              {selectedThread && (
                <ConversationMenu
                  thread={selectedThread}
                  onToggleMute={handleToggleMute}
                  onRemove={handleRemoveConversation}
                  onInfo={setInfoThread}
                  disabled={managingThreadId === selectedThread.id}
                />
              )}
            </div>

            {limitedPartners.length > 0 && (
              <div
                role="status"
                className="mx-4 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg"
              >
                {directPartnerLimited
                  ? `${limitedPartners[0].name} has limited app access. They can’t see messages until access is restored.`
                  : `${limitedPartners.map((p) => p.name).join(', ')} ${
                      limitedPartners.length === 1 ? 'has limited access and won’t' : 'have limited access and won’t'
                    } see this chat until access is restored.`}
              </div>
            )}
            {managementError && (
              <div role="alert" className="mx-4 mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg md:hidden">
                {managementError}
              </div>
            )}

            {/* Add-participant picker (group threads) */}
            {showAddParticipant && selectedThread && (
              <div className="border-b border-gray-100 bg-off-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-navy">Add to this chat</p>
                  <button
                    onClick={() => setShowAddParticipant(false)}
                    className="p-1 hover:bg-gray-200 rounded"
                    aria-label="Close add participant"
                  >
                    <X size={16} />
                  </button>
                </div>
                {!selectedThread.forum && (
                  <div className="space-y-2 mb-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                      <input
                        type="text"
                        placeholder="Search members…"
                        value={addParticipantSearch}
                        onChange={(e) => setAddParticipantSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        aria-label="Role"
                        value={groupRoleFilter}
                        onChange={(e) => setGroupRoleFilter(e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-navy bg-white focus:ring-2 focus:ring-navy outline-none"
                      >
                        {GROUP_ROLE_FILTERS.map((option) => (
                          <option key={option.value || 'all'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label="Forum"
                        value={groupForumFilter}
                        onChange={(e) => setGroupForumFilter(e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-navy bg-white focus:ring-2 focus:ring-navy outline-none"
                      >
                        <option value="">All forums</option>
                        <option value="none">No forum</option>
                        {myForums.map((forum) => (
                          <option key={forum.id} value={String(forum.id)}>
                            {forum.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {addCandidateUsers.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => handleAddParticipant(u.id)}
                        disabled={addingParticipant}
                        className="w-full px-3 py-2 flex items-center gap-2 bg-white rounded-lg hover:bg-gray-50 transition text-left disabled:opacity-50"
                      >
                        <div className="w-7 h-7 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">
                            {u.full_name.charAt(0)}
                          </span>
                        </div>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-navy truncate">
                            {u.full_name}
                            <AccessBadges person={u} className="ml-2 align-middle" />
                          </span>
                          {u.limit_app_access ? (
                            <span className="block text-xs text-amber-800 truncate">
                              Can’t see messages while access is limited
                            </span>
                          ) : forumNames(u.forums) ? (
                            <span className="block text-xs text-gray-500 truncate">
                              {roleLabel(u.role)} • {forumNames(u.forums)}
                            </span>
                          ) : (
                            <span className="block text-xs text-gray-500 truncate">
                              {roleLabel(u.role)}
                            </span>
                          )}
                        </span>
                      </button>
                    ))}
                  {addCandidateUsers.length === 0 && (
                    <p className="text-sm text-gray-500 px-1 py-2">
                      {selectedThread.forum
                        ? 'Everyone in this forum is already in the chat.'
                        : 'No one else matches those filters.'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Messages */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {!selectedThread && draftRecipient ? (
                <div className="h-full flex items-center justify-center text-center">
                  <div>
                    <div className="w-14 h-14 bg-gold rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-white font-bold text-xl">
                        {draftRecipient.full_name.charAt(0)}
                      </span>
                    </div>
                    <p className="font-semibold text-navy">
                      {draftRecipient.full_name}
                      <AccessBadges person={draftRecipient} className="ml-2 align-middle" />
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {draftRecipient.limit_app_access
                        ? 'Their app access is limited — they can’t see messages until it’s restored.'
                        : 'Send a message to start the conversation.'}
                    </p>
                  </div>
                </div>
              ) : Boolean(messagesError) && messages.length === 0 ? (
                <div role="alert" className="flex h-full items-center justify-center text-center">
                  <div>
                    <AlertCircle className="mx-auto text-red-500" size={32} />
                    <p className="mt-2 text-sm font-medium text-red-700">Could not load messages.</p>
                    <button
                      type="button"
                      onClick={() => void refreshMessages()}
                      className="mt-3 inline-flex items-center gap-1 rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white"
                    >
                      <RefreshCw size={14} /> Try again
                    </button>
                  </div>
                </div>
              ) : messagesInitialLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="w-full max-w-md space-y-3 px-5" role="status">
                    <div className="h-12 w-3/4 animate-pulse rounded-2xl bg-gray-100" />
                    <div className="ml-auto h-12 w-2/3 animate-pulse rounded-2xl bg-navy/10" />
                    <span className="sr-only">Loading messages…</span>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-center">
                  <p className="text-sm text-gray-400">No messages yet. Say hello!</p>
                </div>
              ) : null}
              {messages.map((message, index) => {
                const isOwnMessage = message.sender.id === user?.id;
                const previous = index > 0 ? messages[index - 1] : null;
                const showDaySeparator =
                  !previous ||
                  new Date(previous.created_at).toDateString() !==
                    new Date(message.created_at).toDateString();
                return (
                  <div key={message.id}>
                    {showDaySeparator && (
                      <div className="flex justify-center pb-4 pt-1">
                        <span className="text-xs font-medium text-gray-400 bg-off-white px-3 py-1 rounded-full">
                          {formatDaySeparator(message.created_at)}
                        </span>
                      </div>
                    )}
                    <div
                      className={`flex gap-3 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                    {!isOwnMessage && (
                      <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">
                          {message.sender.first_name?.[0] || message.sender.username[0]}
                        </span>
                      </div>
                    )}
                    <div className={`group max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                      {!isOwnMessage && (
                        <p className="text-xs text-gray-500 mb-1">
                          {message.sender.full_name || message.sender.username}
                        </p>
                      )}
                      {editingMessageId === message.id ? (
                        <div className="flex flex-col gap-2">
                          <div className="relative">
                            <textarea
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                              rows={2}
                              name="if-message-edit"
                              {...messageTextProps}
                              className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy outline-none text-sm text-gray-800"
                            />
                            <MicButton
                              value={editingContent}
                              onChange={setEditingContent}
                              className="absolute bottom-2 right-2"
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={cancelEditMessage}
                              className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSaveEdit(message.id)}
                              disabled={!editingContent.trim()}
                              className="px-3 py-1 text-sm bg-navy text-white rounded-lg hover:bg-navy-dark disabled:opacity-50"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : message.content_is_placeholder && Boolean(message.images?.length) ? (
                        <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          <ScreenshotStrip images={message.images ?? []} size="photo" />
                        </div>
                      ) : (
                        <div
                          className={`p-3 rounded-2xl ${
                            isOwnMessage
                              ? 'bg-navy text-white rounded-br-md'
                              : 'bg-off-white text-gray-800 rounded-bl-md'
                          }`}
                        >
                          {Boolean(message.images?.length) && (
                            <div className={`mb-2 ${message.images?.length === 1 ? '-mx-1.5 -mt-1.5' : ''}`}>
                              <ScreenshotStrip images={message.images ?? []} size="photo" />
                            </div>
                          )}
                          {!message.content_is_placeholder && (
                            <p className="whitespace-pre-wrap">{renderMessageContent(message.content)}</p>
                          )}
                        </div>
                      )}
                      {editingMessageId !== message.id && (
                        <ReactionChips
                          reactions={message.reactions ?? []}
                          onToggle={(emoji) => void handleToggleReaction(message.id, emoji)}
                          align={isOwnMessage ? 'right' : 'left'}
                          currentUserId={user?.id}
                          showWho={(selectedThread?.participants.length ?? 0) > 2}
                        />
                      )}
                      <div className={`flex items-center gap-2 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                        <p className="text-xs text-gray-400">
                          {new Date(message.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {message.is_edited && ' • edited'}
                        </p>
                        {editingMessageId !== message.id && (
                          <span className="pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 focus-within:opacity-100 transition flex">
                            <AddReactionButton
                              onPick={(emoji) => void handleToggleReaction(message.id, emoji)}
                              align={isOwnMessage ? 'right' : 'left'}
                            />
                          </span>
                        )}
                        {isOwnMessage && editingMessageId !== message.id && (
                          <span className="pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 transition flex gap-1">
                            {!message.content_is_placeholder && (
                              <button
                                onClick={() => startEditMessage(message)}
                                className="text-xs text-gray-400 hover:text-navy pointer-coarse:px-1.5 pointer-coarse:py-3"
                              >
                                Edit
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteMessage(message.id)}
                              className="text-xs text-gray-400 hover:text-red-600 pointer-coarse:px-1.5 pointer-coarse:py-3"
                            >
                              Delete
                            </button>
                          </span>
                        )}
                      </div>
                    </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-100">
              {sendError && (
                <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                  {sendError}
                </div>
              )}
              {pendingImages.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {pendingImages.map((file, index) => (
                    <LocalPreview
                      key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                      file={file}
                      onRemove={() => setPendingImages(pendingImages.filter((_, i) => i !== index))}
                    />
                  ))}
                </div>
              )}
              <div className="flex gap-2 sm:gap-3">
                <div className="flex items-stretch">
                  {selectedThread && (
                    <>
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={directPartnerLimited || pendingImages.length >= MAX_MESSAGE_PICTURES}
                        aria-label="Add pictures"
                        className="px-2.5 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-navy transition disabled:opacity-50"
                      >
                        <ImagePlus size={20} />
                      </button>
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept={ACCEPTED_SCREENSHOT_TYPES.join(',')}
                        multiple
                        className="hidden"
                        onChange={(event) => {
                          addPendingImages(Array.from(event.target.files || []));
                          event.target.value = '';
                        }}
                      />
                    </>
                  )}
                  <EmojiPickerButton onPick={insertEmoji} disabled={directPartnerLimited} />
                </div>
                <div className="relative flex-1">
                  <AutosizeTextarea
                    ref={composerRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onPaste={(event) => {
                      if (selectedThread) {
                        pasteScreenshots(event, pendingImages, setPendingImages, MAX_MESSAGE_PICTURES, 'pictures');
                      }
                    }}
                    placeholder={
                      directPartnerLimited
                        ? 'They can’t see messages while access is limited'
                        : 'Write a message…'
                    }
                    name="if-message"
                    {...messageTextProps}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    maxHeight={240}
                    disabled={directPartnerLimited}
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  <MicButton
                    value={newMessage}
                    onChange={setNewMessage}
                    className="absolute bottom-2 right-2"
                  />
                </div>
                <button
                  onClick={handleSend}
                  disabled={(!newMessage.trim() && pendingImages.length === 0) || isSending || directPartnerLimited}
                  className="px-4 py-3 bg-navy text-white rounded-xl hover:bg-navy-dark transition disabled:opacity-50"
                  aria-label="Send message"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="mx-auto text-gray-300" size={64} />
              <p className="text-gray-500 mt-4">Select a conversation to start messaging</p>
              <button
                onClick={() => setShowUserDirectory(true)}
                className="mt-4 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition"
              >
                Or Start a New Conversation
              </button>
            </div>
          </div>
        )}
      </div>

      {infoThread && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Conversation info"
          onClick={() => setInfoThread(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-navy">People in this chat</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {infoThread.participants.length} {infoThread.participants.length === 1 ? 'person' : 'people'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInfoThread(null)}
                className="p-1 text-gray-400 hover:text-navy"
                aria-label="Close info"
              >
                <X size={18} />
              </button>
            </div>
            <ul className="flex-1 overflow-y-auto divide-y divide-gray-50">
              {infoThread.participants.map((person) => (
                <li key={person.id}>
                  <Link
                    to={`/members/${person.id}`}
                    onClick={() => setInfoThread(null)}
                    className="px-5 py-3 flex items-center gap-3 hover:bg-off-white transition"
                  >
                    <div className="w-9 h-9 rounded-full bg-navy text-white flex items-center justify-center text-sm font-semibold">
                      {(person.full_name || person.username || '?').charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-navy truncate">
                        {person.full_name || person.username}
                        {person.id === user?.id ? ' (you)' : ''}
                      </p>
                      {person.role && (
                        <p className="text-xs text-gray-500">{roleLabel(person.role)}</p>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            {infoThread.kind === 'group' && (infoThread.forum || canCreateOrgGroup) && (
              <div className="px-5 py-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedThread(infoThread);
                    setInfoThread(null);
                    openAddParticipant();
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark"
                >
                  <UserPlus size={16} />
                  Add people
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
